import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Badge, Table, Button, Modal, Form,
  Spinner, Alert, Accordion,
} from 'react-bootstrap';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faSyncAlt, faFileExcel,
  faBoxOpen, faChartLine, faInfoCircle, faCheckCircle,
  faTimes, faSave,
} from '@fortawesome/free-solid-svg-icons';
import {
  getCespiti, creaCespite, aggiornaCespite, eliminaCespite,
  getPianoCespite, getAliquotePredefinite, exportRegistroAmmortizzabiliExcel,
} from '../../services/api';

const ANNO_CORRENTE = new Date().getFullYear();
const fmt = (v) => `€ ${parseFloat(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('it-IT') : '—';

const STATO_BADGE = {
  attivo:       { bg: 'success',   label: 'Attivo' },
  dismesso:     { bg: 'danger',    label: 'Dismesso' },
  ammortizzato: { bg: 'secondary', label: 'Ammortizzato' },
};

// ─── Modal piano ammortamento ────────────────────────────────────────────────

function PianoModal({ cespiteId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPianoCespite(cespiteId)
      .then(r => setData(r.data))
      .catch(() => toast.error('Errore caricamento piano'))
      .finally(() => setLoading(false));
  }, [cespiteId]);

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />
          Piano ammortamento — {data?.cespite?.descrizione}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <div className="text-center py-3"><Spinner /></div>}
        {data && (
          <>
            <Row className="mb-3 g-2">
              <Col xs={6} md={3}>
                <small className="text-muted d-block">Costo</small>
                <strong>{fmt(data.cespite.costo_acquisto)}</strong>
              </Col>
              <Col xs={6} md={3}>
                <small className="text-muted d-block">Aliquota</small>
                <strong>{data.cespite.aliquota_ammortamento}%</strong>
              </Col>
              <Col xs={6} md={3}>
                <small className="text-muted d-block">Categoria</small>
                <strong>{data.cespite.categoria}</strong>
              </Col>
              <Col xs={6} md={3}>
                <small className="text-muted d-block">Data acquisto</small>
                <strong>{fmtDate(data.cespite.data_acquisto)}</strong>
              </Col>
            </Row>
            {data.cespite.sotto_soglia && (
              <Alert variant="info" className="small mb-2">
                <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                Costo ≤ €516,46 — deduzione immediata nell'anno di acquisto.
              </Alert>
            )}
            <Table size="sm" bordered>
              <thead className="table-dark">
                <tr>
                  <th>Anno</th>
                  <th className="text-end">Quota</th>
                  <th className="text-end">Fondo amm.</th>
                  <th className="text-end">Valore residuo</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {data.piano.map((p, i) => (
                  <tr key={i}
                    style={{ backgroundColor: p.anno === ANNO_CORRENTE ? '#fff9e6' : undefined }}
                  >
                    <td className="fw-bold">
                      {p.anno}
                      {p.anno === ANNO_CORRENTE && (
                        <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: '0.7rem' }}>
                          anno corrente
                        </Badge>
                      )}
                    </td>
                    <td className="text-end">{fmt(p.quota)}</td>
                    <td className="text-end">{fmt(p.fondo)}</td>
                    <td className="text-end">
                      {p.residuo === 0
                        ? <FontAwesomeIcon icon={faCheckCircle} className="text-success" title="Completamente ammortizzato" />
                        : fmt(p.residuo)}
                    </td>
                    <td className="small text-muted">{p.note}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Chiudi</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Modal form cespite ──────────────────────────────────────────────────────

function CespiteModal({ cespite, aliquote, onClose, onSave }) {
  const isEdit = !!cespite;
  const [form, setForm] = useState({
    descrizione: cespite?.descrizione || '',
    categoria: cespite?.categoria || (aliquote[0]?.categoria || ''),
    aliquota_ammortamento: cespite?.aliquota_ammortamento ?? (aliquote[0]?.aliquota || 15),
    data_acquisto: cespite?.data_acquisto || new Date().toISOString().split('T')[0],
    costo_acquisto: cespite?.costo_acquisto ?? '',
    iva_detraibile: cespite?.iva_detraibile ?? true,
    fornitore_denominazione: cespite?.fornitore_denominazione || '',
    metodo: cespite?.metodo || 'ordinario',
    stato: cespite?.stato || 'attivo',
    data_dismissione: cespite?.data_dismissione || '',
    note: cespite?.note || '',
  });
  const [saving, setSaving] = useState(false);

  const handleCategoriaChange = (cat) => {
    const found = aliquote.find(a => a.categoria === cat);
    setForm(f => ({
      ...f,
      categoria: cat,
      aliquota_ammortamento: found ? found.aliquota : f.aliquota_ammortamento,
    }));
  };

  const costo = parseFloat(form.costo_acquisto) || 0;
  const sottoSoglia = costo > 0 && costo <= 516.46;

  const handleSave = async () => {
    if (!form.descrizione || !form.costo_acquisto || !form.data_acquisto) {
      toast.error('Descrizione, costo e data acquisto sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        costo_acquisto: parseFloat(form.costo_acquisto),
        aliquota_ammortamento: parseFloat(form.aliquota_ammortamento),
        fornitore_denominazione: form.fornitore_denominazione || null,
        data_dismissione: form.data_dismissione || null,
        note: form.note || null,
      };
      if (isEdit) {
        await aggiornaCespite(cespite.id, payload);
        toast.success('Cespite aggiornato');
      } else {
        await creaCespite(payload);
        toast.success('Cespite registrato');
      }
      onSave();
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          <FontAwesomeIcon icon={isEdit ? faEdit : faPlus} className="me-2" />
          {isEdit ? 'Modifica cespite' : 'Nuovo bene ammortizzabile'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-2">
          <Col md={12}>
            <Form.Label className="small">Descrizione *</Form.Label>
            <Form.Control size="sm" value={form.descrizione}
              onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))}
              placeholder="es. Scaffalatura metallo reparto DVD" />
          </Col>

          <Col md={6}>
            <Form.Label className="small">Categoria (DM 31/12/1988) *</Form.Label>
            <Form.Select size="sm" value={form.categoria}
              onChange={e => handleCategoriaChange(e.target.value)}>
              {aliquote.map(a => (
                <option key={a.categoria} value={a.categoria}>
                  {a.categoria} ({a.aliquota}%)
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Label className="small">Aliquota % *</Form.Label>
            <Form.Control size="sm" type="number" step="0.01" min="1" max="100"
              value={form.aliquota_ammortamento}
              onChange={e => setForm(f => ({ ...f, aliquota_ammortamento: e.target.value }))} />
          </Col>

          <Col md={3}>
            <Form.Label className="small">Data acquisto *</Form.Label>
            <Form.Control size="sm" type="date" value={form.data_acquisto}
              onChange={e => setForm(f => ({ ...f, data_acquisto: e.target.value }))} />
          </Col>

          <Col md={3}>
            <Form.Label className="small">Costo acquisto (€) *</Form.Label>
            <Form.Control size="sm" type="number" step="0.01" min="0"
              value={form.costo_acquisto}
              onChange={e => setForm(f => ({ ...f, costo_acquisto: e.target.value }))} />
          </Col>

          <Col md={3}>
            <Form.Label className="small">IVA detraibile</Form.Label>
            <Form.Select size="sm" value={form.iva_detraibile.toString()}
              onChange={e => setForm(f => ({ ...f, iva_detraibile: e.target.value === 'true' }))}>
              <option value="true">Sì (base = imponibile)</option>
              <option value="false">No (base = lordo IVA)</option>
            </Form.Select>
          </Col>

          <Col md={6}>
            <Form.Label className="small">Fornitore</Form.Label>
            <Form.Control size="sm" value={form.fornitore_denominazione}
              onChange={e => setForm(f => ({ ...f, fornitore_denominazione: e.target.value }))}
              placeholder="es. MediaWorld SpA" />
          </Col>

          {isEdit && (
            <>
              <Col md={3}>
                <Form.Label className="small">Stato</Form.Label>
                <Form.Select size="sm" value={form.stato}
                  onChange={e => setForm(f => ({ ...f, stato: e.target.value }))}>
                  <option value="attivo">Attivo</option>
                  <option value="dismesso">Dismesso</option>
                  <option value="ammortizzato">Ammortizzato</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small">Data dismissione</Form.Label>
                <Form.Control size="sm" type="date" value={form.data_dismissione}
                  onChange={e => setForm(f => ({ ...f, data_dismissione: e.target.value }))} />
              </Col>
            </>
          )}

          <Col md={12}>
            <Form.Label className="small">Note</Form.Label>
            <Form.Control as="textarea" rows={2} size="sm" value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
          </Col>
        </Row>

        {sottoSoglia && (
          <Alert variant="info" className="mt-3 mb-0 small">
            <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
            Costo ≤ €516,46 — questo bene sarà <strong>dedotto interamente</strong> nell'anno di acquisto (art. 102 TUIR).
          </Alert>
        )}
        {costo > 516.46 && form.aliquota_ammortamento && (
          <Alert variant="light" className="mt-3 mb-0 small border">
            <strong>Prima annualità (dimezzata):</strong> {fmt(costo * parseFloat(form.aliquota_ammortamento) / 100 * 0.5)}
            {' · '}
            <strong>Anni successivi:</strong> {fmt(costo * parseFloat(form.aliquota_ammortamento) / 100)} / anno
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} className="me-1" />Annulla
        </Button>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-1" /> : <FontAwesomeIcon icon={faSave} className="me-1" />}
          {isEdit ? 'Salva modifiche' : 'Registra bene'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Componente principale ──────────────────────────────────────────────────

export default function RegistroBeniAmmortizzabili() {
  const [cespiti, setCespiti] = useState([]);
  const [aliquote, setAliquote] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroStato, setFiltroStato] = useState('');
  const [annoExport, setAnnoExport] = useState(ANNO_CORRENTE);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [pianoTarget, setPianoTarget] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, aRes] = await Promise.all([
        getCespiti(filtroStato || undefined),
        getAliquotePredefinite(),
      ]);
      setCespiti(cRes.data);
      setAliquote(aRes.data);
    } catch {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [filtroStato]);

  useEffect(() => { load(); }, [load]);

  const handleElimina = async (c) => {
    if (!window.confirm(`Eliminare "${c.descrizione}"?`)) return;
    try {
      await eliminaCespite(c.id);
      toast.success('Cespite eliminato');
      load();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportRegistroAmmortizzabiliExcel(annoExport);
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `registro_beni_ammortizzabili_${annoExport}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Registro esportato');
    } catch {
      toast.error('Errore export Excel');
    } finally {
      setExporting(false);
    }
  };

  // KPI
  const attivi      = cespiti.filter(c => c.stato === 'attivo').length;
  const totCosto    = cespiti.reduce((s, c) => s + c.costo_acquisto, 0);

  return (
    <div className="container-fluid py-3">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faBoxOpen} className="me-2 text-primary" />
            Registro Beni Ammortizzabili
          </h4>
          <small className="text-muted">Art. 16 DPR 600/73 — Aliquote DM 31/12/1988 — Art. 102 TUIR</small>
        </Col>
        <Col xs="auto" className="d-flex gap-2 align-items-center flex-wrap">
          <Form.Select value={filtroStato} onChange={e => setFiltroStato(e.target.value)} style={{ width: 130 }}>
            <option value="">Tutti</option>
            <option value="attivo">Attivi</option>
            <option value="dismesso">Dismessi</option>
            <option value="ammortizzato">Ammortizzati</option>
          </Form.Select>
          <Button variant="outline-secondary" size="sm" onClick={load} title="Aggiorna">
            <FontAwesomeIcon icon={faSyncAlt} />
          </Button>
          <Form.Select value={annoExport} onChange={e => setAnnoExport(+e.target.value)} style={{ width: 90 }}>
            {[ANNO_CORRENTE, ANNO_CORRENTE - 1, ANNO_CORRENTE + 1].map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </Form.Select>
          <Button variant="outline-success" size="sm" onClick={handleExport} disabled={exporting}>
            {exporting ? <Spinner size="sm" /> : <FontAwesomeIcon icon={faFileExcel} />}
            {' '}Registro Excel
          </Button>
          <Button variant="primary" size="sm" onClick={() => { setEditTarget(null); setShowForm(true); }}>
            <FontAwesomeIcon icon={faPlus} className="me-1" />Nuovo bene
          </Button>
        </Col>
      </Row>

      {/* KPI */}
      <Row className="g-2 mb-3">
        <Col xs={6} md={3}>
          <Card className="text-center border-0 bg-primary bg-opacity-10 py-2">
            <div className="small text-primary fw-bold">Beni attivi</div>
            <div className="fs-4 fw-bold text-primary">{attivi}</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="text-center border-0 bg-light py-2">
            <div className="small text-muted fw-bold">Totale cespiti</div>
            <div className="fs-5 fw-bold">{cespiti.length}</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="text-center border-0 bg-success bg-opacity-10 py-2">
            <div className="small text-success fw-bold">Costo totale</div>
            <div className="fw-bold text-success">{fmt(totCosto)}</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="text-center border-0 bg-warning bg-opacity-10 py-2">
            <div className="small text-warning fw-bold">Anno export</div>
            <div className="fw-bold text-warning">{annoExport}</div>
          </Card>
        </Col>
      </Row>

      {loading && <div className="text-center py-5"><Spinner /></div>}

      {!loading && cespiti.length === 0 && (
        <Alert variant="info">
          Nessun bene registrato{filtroStato ? ` (filtro: ${filtroStato})` : ''}.
          Usa "Nuovo bene" per inserire un cespite.
        </Alert>
      )}

      {!loading && cespiti.length > 0 && (
        <Table responsive bordered hover size="sm">
          <thead className="table-dark">
            <tr>
              <th>Descrizione</th>
              <th>Categoria</th>
              <th className="text-end">Costo</th>
              <th className="text-end">Aliquota</th>
              <th>Data acquisto</th>
              <th>Fornitore</th>
              <th>Stato</th>
              <th style={{ width: 100 }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {cespiti.map(c => (
              <tr key={c.id}
                style={{ opacity: c.stato !== 'attivo' ? 0.65 : 1 }}>
                <td>
                  <span className="fw-bold">{c.descrizione}</span>
                  {c.sotto_soglia && (
                    <Badge bg="info" className="ms-1" style={{ fontSize: '0.7rem' }}>
                      Ded. immediata
                    </Badge>
                  )}
                </td>
                <td className="small">{c.categoria}</td>
                <td className="text-end">{fmt(c.costo_acquisto)}</td>
                <td className="text-end">{c.aliquota_ammortamento}%</td>
                <td className="small">{fmtDate(c.data_acquisto)}</td>
                <td className="small text-muted">{c.fornitore_denominazione || '—'}</td>
                <td>
                  <Badge bg={STATO_BADGE[c.stato]?.bg || 'secondary'}>
                    {STATO_BADGE[c.stato]?.label || c.stato}
                  </Badge>
                </td>
                <td>
                  <Button size="sm" variant="outline-info" className="me-1"
                    onClick={() => setPianoTarget(c.id)}
                    title="Piano ammortamento">
                    <FontAwesomeIcon icon={faChartLine} />
                  </Button>
                  <Button size="sm" variant="outline-secondary" className="me-1"
                    onClick={() => { setEditTarget(c); setShowForm(true); }}>
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                  <Button size="sm" variant="outline-danger"
                    onClick={() => handleElimina(c)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Alert variant="light" className="mt-2 small border">
        <FontAwesomeIcon icon={faInfoCircle} className="me-1 text-muted" />
        Prima annualità: quota <strong>dimezzata</strong> (50% aliquota). Beni ≤ €516,46: deduzione immediata.
        Usa "Registro Excel" per esportare il registro ufficiale con piani individuali.
      </Alert>

      {showForm && (
        <CespiteModal
          cespite={editTarget}
          aliquote={aliquote}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
          onSave={() => { setShowForm(false); setEditTarget(null); load(); }}
        />
      )}

      {pianoTarget && (
        <PianoModal
          cespiteId={pianoTarget}
          onClose={() => setPianoTarget(null)}
        />
      )}
    </div>
  );
}
